// ===========================================
// Email Log Report (2026-09-09) — the tenth report
// ===========================================
// How many emails were sent, delivered, opened, bounced, marked as spam — and
// how many were NOT SENT — filterable by type. Built on the rows the backend
// writes for every send attempt, so "not sent" is a number here, never an
// absence. Three states are rendered distinctly: loading, error, empty —
// an empty table must never read as "the school sent no email".

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  ErrorOutline as ErrorOutlineIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Drafts as DraftsIcon,
  ReportProblem as ReportProblemIcon,
  Block as BlockIcon,
  ScheduleSend as ScheduleSendIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/common/PageHeader';
import ExportButtons from '../../../components/reports/ExportButtons';
import { useEmailLogReport, useExportEmailLogReport } from '../../../hooks/useReports';
import {
  EMAIL_CATEGORY_OPTIONS,
  EMAIL_SEND_OUTCOME_OPTIONS,
  EMAIL_STATUS_OPTIONS,
  type EmailLogReportFilters,
  type EmailLogRow,
} from '../../../api/reports.api';
import { formatInSchoolTz } from '../../../utils/dateUtils';

// ---- helpers ----

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Default period: the last 30 days, so the page shows data on arrival. */
export function defaultEmailLogFilters(now = new Date()): EmailLogReportFilters {
  const start = new Date(now);
  start.setDate(start.getDate() - 29);
  return { startDate: dayKey(start), endDate: dayKey(now) };
}

const INSTANT_FORMAT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

export function describeOutcome(row: Pick<EmailLogRow, 'sendOutcome' | 'providerError'>): {
  label: string;
  color: 'default' | 'error' | 'warning';
  detail?: string;
} {
  switch (row.sendOutcome) {
    case 'ACCEPTED':
      return { label: 'Sent', color: 'default' };
    case 'NOT_CONFIGURED':
      return { label: 'Not sent: Mailgun not configured', color: 'error', detail: row.providerError ?? undefined };
    case 'REJECTED':
      return { label: 'Not sent: rejected', color: 'error', detail: row.providerError ?? undefined };
    case 'RETRIES_EXHAUSTED':
      return { label: 'Not sent: retries exhausted', color: 'error', detail: row.providerError ?? undefined };
    default:
      return { label: 'Pending', color: 'warning', detail: 'Opened but never closed — in flight, a crash mid-send, or a legacy row whose reason was never recorded.' };
  }
}

function statusColor(status: EmailLogRow['status']): 'default' | 'success' | 'error' {
  if (status === 'DELIVERED') return 'success';
  if (status === 'FAILED' || status === 'BOUNCED') return 'error';
  return 'default';
}

// ---- stat card (same vocabulary as CustomEmailAnalyticsPage) ----

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'info' | 'warning' | 'error';
  percentage?: number;
  caption?: string;
  tooltip?: string;
}

function StatCard({ title, value, icon, color, percentage, caption, tooltip }: StatCardProps) {
  const card = (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}.lighter`, color: `${color}.main` }}>{icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={600}>
              {value.toLocaleString()}
            </Typography>
            {percentage !== undefined && (
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(percentage, 100)}
                  color={color}
                  sx={{ height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {percentage.toFixed(1)}%{caption ? ` · ${caption}` : ''}
                </Typography>
              </Box>
            )}
            {percentage === undefined && caption && (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

// ---- page ----

export default function EmailLogReportPage() {
  const [filters, setFilters] = useState<EmailLogReportFilters>(() => defaultEmailLogFilters());
  const [applied, setApplied] = useState<EmailLogReportFilters>(() => defaultEmailLogFilters());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const query = useMemo(
    () => ({ ...applied, page: page + 1, pageSize: rowsPerPage }),
    [applied, page, rowsPerPage]
  );
  const { data, isLoading, isError, refetch } = useEmailLogReport(query);
  const exportMutation = useExportEmailLogReport();

  const set = (patch: Partial<EmailLogReportFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const apply = () => {
    setApplied(filters);
    setPage(0);
  };
  const reset = () => {
    const d = defaultEmailLogFilters();
    setFilters(d);
    setApplied(d);
    setPage(0);
  };

  const s = data?.summary;
  const tz = s?.timeZone ?? 'Australia/Sydney';
  const notSentTooltip = s
    ? `Not configured ${s.notSentByOutcome.NOT_CONFIGURED} · rejected ${s.notSentByOutcome.REJECTED} · retries exhausted ${s.notSentByOutcome.RETRIES_EXHAUSTED} · legacy, reason not recorded ${s.notSentByOutcome.LEGACY_FAILED}`
    : undefined;

  return (
    <Box>
      <PageHeader
        title="Email Log"
        subtitle="Every email the school sent — and every one it tried to"
        icon={<EmailIcon />}
        action={
          <ExportButtons
            onExport={(format) => exportMutation.mutate({ format, filters: applied })}
            loading={exportMutation.isPending}
            disabled={!data || data.summary.total === 0}
          />
        }
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                value={filters.startDate ?? ''}
                onChange={(e) => set({ startDate: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                value={filters.endDate ?? ''}
                onChange={(e) => set({ endDate: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Type"
                size="small"
                fullWidth
                value={filters.emailCategory ?? ''}
                onChange={(e) => set({ emailCategory: (e.target.value || undefined) as EmailLogReportFilters['emailCategory'] })}
              >
                <MenuItem value="">All types</MenuItem>
                {EMAIL_CATEGORY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Outcome"
                size="small"
                fullWidth
                value={filters.sendOutcome ?? ''}
                onChange={(e) => set({ sendOutcome: (e.target.value || undefined) as EmailLogReportFilters['sendOutcome'] })}
              >
                <MenuItem value="">All outcomes</MenuItem>
                {EMAIL_SEND_OUTCOME_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                label="Delivery"
                size="small"
                fullWidth
                value={filters.status ?? ''}
                onChange={(e) => set({ status: (e.target.value || undefined) as EmailLogReportFilters['status'] })}
              >
                <MenuItem value="">Any</MenuItem>
                {EMAIL_STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Recipient"
                size="small"
                fullWidth
                placeholder="part of an address"
                value={filters.recipientEmail ?? ''}
                onChange={(e) => set({ recipientEmail: e.target.value || undefined })}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={apply}>
                  Apply
                </Button>
                <Button variant="text" onClick={reset}>
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Three states, distinctly */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          The email log could not be loaded. Nothing below reflects what was sent.
        </Alert>
      )}
      {!isLoading && !isError && data && data.summary.total === 0 && (
        <Alert severity="info">No emails were attempted in this period with these filters.</Alert>
      )}

      {!isLoading && !isError && data && s && data.summary.total > 0 && (
        <>
          {/* Summary */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="Emails" value={s.total} icon={<EmailIcon />} color="primary" caption={`${s.accepted.toLocaleString()} accepted by Mailgun`} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                title="Not sent"
                value={s.notSent}
                icon={<ErrorOutlineIcon />}
                color="error"
                caption="never reached Mailgun"
                tooltip={notSentTooltip}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="Delivered" value={s.delivered} icon={<MarkEmailReadIcon />} color="success" percentage={s.deliveryRate} caption="of accepted" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                title="Opened"
                value={s.opened}
                icon={<DraftsIcon />}
                color="info"
                percentage={s.openRate}
                caption="of delivered · directional"
                tooltip="Apple Mail Privacy Protection pre-fetches images (counts opens that never happened); image-blocking clients hide real ones. Treat as a trend, not a count."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="Bounced" value={s.bounced} icon={<BlockIcon />} color="warning" caption="permanent failures" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard title="Marked as spam" value={s.complained} icon={<ReportProblemIcon />} color="error" />
            </Grid>
            {/* A snapshot of NOW, not of the period: mail still to go out
                across every in-flight campaign, however long ago it started. */}
            {s.inFlight && (s.inFlight.emailsQueued > 0 || s.inFlight.campaignsQueued > 0) && (
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <StatCard
                  title="Queued now"
                  value={s.inFlight.emailsQueued}
                  icon={<ScheduleSendIcon />}
                  // ⚠️ `info.main` is #4580E4 — the SAME hex as `primary.main`
                  // — so this card is colour-identical to "Opened". Logged as
                  // pre-existing; changing the palette is not this change's job.
                  color="info"
                  caption={
                    s.inFlight.campaignsQueued > 0
                      ? `${s.inFlight.campaignsSending} sending · ${s.inFlight.campaignsQueued} waiting`
                      : `across ${s.inFlight.campaignsSending} campaign${s.inFlight.campaignsSending === 1 ? '' : 's'}`
                  }
                  tooltip="Recipients not yet delivered across campaigns that are sending right now. Independent of the date range above — a campaign started earlier is still counted while it runs."
                />
              </Grid>
            )}
          </Grid>

          {/* By type */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                By type
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Emails</TableCell>
                      <TableCell align="right">Not sent</TableCell>
                      <TableCell align="right">Delivered</TableCell>
                      <TableCell align="right">Opened</TableCell>
                      <TableCell align="right">Bounced</TableCell>
                      <TableCell align="right">Spam</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.byCategory.map((c) => (
                      <TableRow key={c.emailCategory ?? 'none'}>
                        <TableCell>{c.label}</TableCell>
                        <TableCell align="right">{c.total}</TableCell>
                        <TableCell align="right" sx={{ color: c.notSent > 0 ? 'error.main' : undefined, fontWeight: c.notSent > 0 ? 600 : undefined }}>
                          {c.notSent}
                        </TableCell>
                        <TableCell align="right">{c.delivered}</TableCell>
                        <TableCell align="right">{c.opened}</TableCell>
                        <TableCell align="right">{c.bounced}</TableCell>
                        <TableCell align="right">{c.complained}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Rows */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Emails
              </Typography>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Attempted</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Delivery</TableCell>
                      <TableCell>Opened</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.rows.map((r) => {
                      const outcome = describeOutcome(r);
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatInSchoolTz(r.createdAt, tz, INSTANT_FORMAT)}</TableCell>
                          <TableCell>{r.recipientEmail ?? '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.subject ?? '—'}
                          </TableCell>
                          <TableCell>{r.categoryLabel}</TableCell>
                          <TableCell>
                            <Tooltip title={outcome.detail ?? ''}>
                              <Chip label={outcome.label} size="small" color={outcome.color} variant={outcome.color === 'default' ? 'outlined' : 'filled'} />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip label={r.status} size="small" color={statusColor(r.status)} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.openedAt ? formatInSchoolTz(r.openedAt, tz, INSTANT_FORMAT) : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={data.pagination.total}
                page={page}
                onPageChange={(_e, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100]}
              />
              <Typography variant="caption" color="text.secondary">
                Times are the school&apos;s clock ({tz}). The type filter counts from the day the email log shipped; earlier email was never categorised.
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
